import React, {
  useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle,
} from 'react';
import {
  Mail, Phone, PhoneCall, Send, ExternalLink, Search, Eye, KeyRound,
  XCircle, PauseCircle, Check, Loader2, Home,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getNotesForIdentity } from '../../../api/contact';
import { getContactSearchStatus, setContactSearchStatus } from '../../analytics/api/analyticsApi';
import { formatMelbourneShortDate } from '../../../utils/melbourneTime';
import ContactLink from './ContactLink';
import RentedReturnPicker, { DEFAULT_RENTED_RETURN } from './RentedReturnPicker';

/**
 * The buyer status + quick-actions bar at the top of the Buying preferences
 * modal, so an agent can see and change where a buyer is in their search
 * without leaving it (George Bottas, CRM-2026-00097).
 *
 * Christian Gravias, 10 Oct 2026: the modal previously showed only a single
 * "Recently Purchased" toggle, while the buyer-facing portal lets them pick one
 * of four states - Actively looking, Keeping an eye on the market, I've
 * purchased, I'm no longer looking. This bar now mirrors that same four-state
 * search status, so what the agent sees and sets here matches the portal
 * exactly (and the older Recently Purchased flag is kept in step server-side by
 * the buyer_lifecycle service, which syncs the purchased branch down to it).
 *
 * Status source of truth: Contact.buyer_status (mirrored to the portal
 * account), read/written through /buyer-portal/contacts/<id>/search-status/
 * with source='agent'. The quick Email, Call and Full profile buttons are
 * unchanged, since Who-to-Call has no property context.
 *
 * Props:
 *   buyer  { contactId?/contact_id?, buyerId?/id?, email?, name?, phone? }
 *   onStatusChange(snapshot)  optional, fired after a status change succeeds.
 *
 * Ref: `savePending()` saves a picked-but-unsaved status and resolves true, or
 * false if the save failed. The modals call it from their Save Preferences
 * button: agents pick "Actively looking" and press the big Save at the bottom,
 * which used to save only the criteria and close, dropping the pick
 * (Genneva Smarrelli, CRM-2026-00134).
 */

// The four answers a buyer has on the portal. "Buyer purchased" resolves to
// purchased_collings / purchased_elsewhere via the "through Collings?" answer,
// and "Actively looking" + the pause toggle resolves to paused - matching
// SearchStatusCard on the portal and core/utils/buyer_lifecycle_choices.py.
// Worded for the agent rather than in the buyer's voice (Christian Gravias,
// DOMA 13374913603: "Edit "I've" to "Buyer""). An agent's "No longer
// looking" is recorded only; the buyer keeps their alerts, so only their own
// answer on the portal or the emailed link unsubscribes them (buyer_lifecycle).
const CHOICES = [
  { id: 'active', label: 'Actively looking', hint: 'Send me everything that matches', icon: Search },
  { id: 'watching', label: 'Keeping an eye on the market', hint: 'Nothing urgent, keep me in the loop', icon: Eye },
  { id: 'purchased', label: 'Buyer purchased', hint: 'Congratulations, tell us a bit more', icon: KeyRound },
  { id: 'not_looking', label: 'No longer looking', hint: 'Noted on their record. Alerts keep going', icon: XCircle },
  // Agent-only (George Bottas, CRM-2026-00132): renting for now, back to
  // buying on a return date, with a call-back reminder for that day.
  { id: 'rented', label: 'Recently rented', hint: 'Renting for now, back to buying later', icon: Home },
];

const PAUSE_CHOICES = [
  { id: '1m', label: '1 month' },
  { id: '3m', label: '3 months' },
  { id: '6m', label: '6 months' },
  { id: 'date', label: 'Choose a date' },
  { id: 'indefinite', label: 'Until turned back on' },
];

// Mirrors NOT_LOOKING_REASON_CHOICES; same ids as the portal's SearchStatusCard.
const REASONS = [
  { id: 'purchased_elsewhere', label: 'Purchased elsewhere' },
  { id: 'plans_changed', label: 'Plans changed' },
  { id: 'finance', label: 'Finance / budget' },
  { id: 'waiting_market', label: 'Waiting for the market' },
  { id: 'not_right_now', label: 'Not looking right now' },
  { id: 'other', label: 'Other' },
];

const PURCHASED_STATUSES = ['purchased_collings', 'purchased_elsewhere'];

const pill = (selected) =>
  `px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
    selected
      ? 'bg-[#E0F7FA] border-[#2DC8DB] text-[#00838F]'
      : 'bg-white border-gray-200 text-gray-600 hover:border-[#55C5D0]'
  }`;

const YesNo = ({ label, value, onChange }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 py-1">
    <span className="text-xs text-gray-700">{label}</span>
    <div className="flex gap-2">
      <button type="button" className={pill(value === true)} onClick={() => onChange(value === true ? null : true)}>Yes</button>
      <button type="button" className={pill(value === false)} onClick={() => onChange(value === false ? null : false)}>No</button>
    </div>
  </div>
);

const BuyerStatusActions = forwardRef(({ buyer, onStatusChange }, ref) => {
  const identity = {
    contactId: buyer?.contactId ?? buyer?.contact_id ?? null,
    buyerId: buyer?.id ?? buyer?.buyerId ?? null,
    email: buyer?.email,
    name: buyer?.name,
    phone: buyer?.phone,
  };
  const { email, phone } = identity;

  const [contactId, setContactId] = useState(identity.contactId);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // The pending edit. `touched` gates the Update button so we never write
  // unless the agent actually picked something.
  const [touched, setTouched] = useState(false);
  const [choice, setChoice] = useState('');
  const [pause, setPause] = useState(false);
  const [pauseOption, setPauseOption] = useState('3m');
  const [pausedUntil, setPausedUntil] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [throughCollings, setThroughCollings] = useState(null);
  const [rentOption, setRentOption] = useState(DEFAULT_RENTED_RETURN);
  const [rentDate, setRentDate] = useState('');

  const applySnapshot = useCallback((status) => {
    setSnapshot(status || null);
    const current = status?.status || '';
    if (PURCHASED_STATUSES.includes(current)) {
      setChoice('purchased');
    } else if (current === 'paused') {
      setChoice('active');
      setPause(true);
      if (status?.paused_until) {
        setPauseOption('date');
        setPausedUntil(status.paused_until);
      } else {
        setPauseOption('indefinite');
      }
    } else if (current === 'rented') {
      setChoice('rented');
      setPause(false);
      setRentOption(status?.paused_until ? 'date' : DEFAULT_RENTED_RETURN);
      setRentDate(status?.paused_until || '');
    } else {
      setChoice(current);
      setPause(false);
    }
    setReason(status?.reason || '');
    setNote(status?.note || '');
    setPurchaseDate(status?.purchase_date || '');
    setThroughCollings(status?.bought_through_collings);
    setTouched(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // notes-for get-or-creates the Contact, so this resolves a contact id for
      // portal-only buyers too; the status endpoint is keyed by that id.
      const res = await getNotesForIdentity(identity);
      const cid = res?.data?.contact_id ?? identity.contactId;
      setContactId(cid);
      if (cid) {
        const data = await getContactSearchStatus(cid);
        applySnapshot(data?.status);
      }
    } catch (e) {
      // A failed status read just leaves the picker empty; the prefs body below
      // still works and the agent can set a status once it loads.
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyer?.id, buyer?.buyerId, buyer?.contactId, buyer?.contact_id, buyer?.email, applySnapshot]);

  // Keyed on who the buyer is, not on the `buyer` object: callers build it
  // inline, and reloading on every parent render wiped an unsaved pick.
  const hasBuyer = !!buyer;
  useEffect(() => { if (hasBuyer) load(); }, [hasBuyer, load]);

  const payload = useMemo(() => {
    if (choice === 'purchased') {
      return {
        status: throughCollings ? 'purchased_collings' : 'purchased_elsewhere',
        purchase_date: purchaseDate || null,
        bought_through_collings: throughCollings,
      };
    }
    if (choice === 'not_looking') {
      return { status: 'not_looking', reason, note: reason === 'other' ? note : '' };
    }
    if (choice === 'rented') {
      return {
        status: 'rented',
        pause_option: rentOption,
        paused_until: rentOption === 'date' ? rentDate : null,
      };
    }
    if (choice === 'active' || choice === 'watching') {
      if (pause) {
        return {
          status: 'paused',
          pause_option: pauseOption,
          paused_until: pauseOption === 'date' ? pausedUntil : null,
        };
      }
      return { status: choice };
    }
    return null;
  }, [choice, pause, pauseOption, pausedUntil, reason, note, purchaseDate, throughCollings, rentOption, rentDate]);

  const pick = (fn) => (...args) => { fn(...args); setTouched(true); };

  // Resolves true when saved, false when it could not be.
  const save = async () => {
    if (!payload || saving) return false;
    if (!contactId) {
      toast.error('Could not identify this buyer yet - try again in a moment.');
      return false;
    }
    if (payload.status === 'paused' && pauseOption === 'date' && !pausedUntil) {
      toast.error('Pick the date their search should restart.');
      return false;
    }
    if (payload.status === 'rented' && rentOption === 'date' && !rentDate) {
      toast.error('Pick the date they should come back to buying.');
      return false;
    }
    setSaving(true);
    try {
      const data = await setContactSearchStatus(contactId, payload);
      applySnapshot(data?.status);
      onStatusChange?.(data?.status);
      toast.success(`${identity.name || 'Buyer'} set to ${data?.status?.status_label || 'updated'}`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Could not update this buyer');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Nothing picked means nothing to save, which is a success for the caller.
  useImperativeHandle(ref, () => ({
    savePending: () => (touched && payload ? save() : Promise.resolve(true)),
  }));

  const actionBtn =
    'inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-700 transition-colors hover:border-[#55C5D0] hover:text-[#3a99a3]';

  const stillLooking = choice === 'active' || choice === 'watching';
  const currentLabel = snapshot?.status_label;
  const pausedUntilLabel = formatMelbourneShortDate(snapshot?.paused_until);

  return (
    <div className="border-b border-gray-100 bg-gray-50/60 px-5 py-3">
      {/* Contact line */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5 text-gray-400" />
          {email || <span className="text-gray-400">No email on file</span>}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Phone className="h-3.5 w-3.5 text-gray-400" />
          {phone || <span className="text-gray-400">No phone on file</span>}
        </span>
      </div>

      {/* Quick actions */}
      <div className="mt-2 flex flex-wrap gap-2">
        <a
          href={email ? `mailto:${email}` : undefined}
          onClick={(e) => { if (!email) { e.preventDefault(); toast.error('No email on file for this buyer.'); } }}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
            email ? 'bg-[#55C5D0] text-white hover:bg-[#47b1bb]' : 'cursor-not-allowed bg-gray-100 text-gray-400'
          }`}
        >
          <Send className="h-3.5 w-3.5" /> Send Email
        </a>
        {phone && (
          <a href={`tel:${phone}`} className={actionBtn}>
            <PhoneCall className="h-3.5 w-3.5" /> Call
          </a>
        )}
        <ContactLink identity={identity} className={actionBtn} title="Open this buyer's full profile">
          <ExternalLink className="h-3.5 w-3.5" /> Full profile
        </ContactLink>
      </div>

      {/* Search status - the same four states the buyer picks on the portal */}
      <div className="mt-3 border-t border-gray-100 pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-extrabold uppercase tracking-wide text-[#00838F]">Buyers Search Status</span>
          {loading ? (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading
            </span>
          ) : currentLabel ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
              <Check className="h-3.5 w-3.5 text-[#00838F]" />
              Currently: <strong className="text-gray-700">{currentLabel}</strong>
              {['paused', 'rented'].includes(snapshot?.status) && pausedUntilLabel && ` until ${pausedUntilLabel}`}
            </span>
          ) : (
            <span className="text-xs text-gray-400">Not set</span>
          )}
        </div>

        {!loading && (
          <>
            {/* Two tiles per row, larger, mirroring the portal's SearchStatusCard
                so the agent sees the same layout the buyer does (Christian
                Gravias, 13 Oct 2026). */}
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CHOICES.map(({ id, label, hint, icon: Icon }) => {
                const selected = choice === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => pick(setChoice)(id)}
                    className={`flex items-start gap-3 text-left px-3 py-3 rounded-xl border transition-all duration-200 hover:shadow-sm ${
                      selected
                        ? 'bg-[#E0F7FA] border-[#2DC8DB] shadow-sm'
                        : 'bg-white border-gray-200 hover:border-[#55C5D0]'
                    }`}
                  >
                    <span className={`p-1.5 rounded-full shrink-0 ${
                      selected ? 'bg-[#2DC8DB]/20 text-[#00838F]' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className={`block text-[13px] font-bold ${selected ? 'text-[#00838F]' : 'text-gray-800'}`}>{label}</span>
                      <span className="block text-[11px] text-gray-500 mt-0.5">{hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {stillLooking && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => pick(setPause)(!pause)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700"
                >
                  <PauseCircle className={`h-4 w-4 ${pause ? 'text-[#00838F]' : 'text-gray-400'}`} />
                  Pause their property alerts
                </button>
                {pause && (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {PAUSE_CHOICES.map(({ id, label }) => (
                        <button key={id} type="button" className={pill(pauseOption === id)} onClick={() => pick(setPauseOption)(id)}>
                          {label}
                        </button>
                      ))}
                    </div>
                    {pauseOption === 'date' && (
                      <input
                        type="date"
                        value={pausedUntil || ''}
                        onChange={(e) => pick(setPausedUntil)(e.target.value)}
                        className="rounded-md border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#55C5D0]"
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {choice === 'rented' && (
              <div className="mt-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <p className="mb-2 text-xs text-gray-500">
                  When might they look to buy again? They come back as Actively looking then, and you get a reminder to call them.
                </p>
                <RentedReturnPicker
                  option={rentOption}
                  date={rentDate}
                  onOptionChange={pick(setRentOption)}
                  onDateChange={pick(setRentDate)}
                />
              </div>
            )}

            {choice === 'purchased' && (
              <div className="mt-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <YesNo label="Did they buy through Collings?" value={throughCollings} onChange={pick(setThroughCollings)} />
                <div className="flex flex-wrap items-center justify-between gap-2 py-1">
                  <span className="text-xs text-gray-700">Purchase date (optional)</span>
                  <input
                    type="date"
                    value={purchaseDate || ''}
                    onChange={(e) => pick(setPurchaseDate)(e.target.value)}
                    className="rounded-md border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#55C5D0]"
                  />
                </div>
              </div>
            )}

            {choice === 'not_looking' && (
              <div className="mt-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <p className="mb-2 text-xs text-gray-500">Why? (optional)</p>
                <div className="flex flex-wrap gap-2">
                  {REASONS.map(({ id, label }) => (
                    <button key={id} type="button" className={pill(reason === id)} onClick={() => pick(setReason)(reason === id ? '' : id)}>
                      {label}
                    </button>
                  ))}
                </div>
                {reason === 'other' && (
                  <input
                    type="text"
                    value={note}
                    maxLength={255}
                    onChange={(e) => pick(setNote)(e.target.value)}
                    placeholder="Tell us more (optional)"
                    className="mt-2 w-full rounded-md border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#55C5D0]"
                  />
                )}
              </div>
            )}

            {touched && payload && (
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#55C5D0] px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#47b1bb] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Update status
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
});

export default BuyerStatusActions;
